import { useEffect, useState } from 'react'
import { Outlet, Navigate, useLocation } from 'react-router-dom'
import { backendApi } from '@/lib/api/backend'

const PUBLIC_PATHS = ['/signin', '/signup', '/login']

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.includes(pathname) || pathname.startsWith('/auth/')
}

interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  user: unknown | null
}

export function AuthGuard() {
  const location = useLocation()

  // Safety net: if this guard renders for a public route, redirect to /signin
  // without making any API calls (prevents redirect loops and unnecessary 401s)
  if (isPublicPath(location.pathname)) {
    return <Navigate to="/signin" replace />
  }

  return <AuthGuardInner />
}

function AuthGuardInner() {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
  })
  const location = useLocation()

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const session = await backendApi.auth.getSession()
        setAuthState({
          isAuthenticated: !!session.user,
          isLoading: false,
          user: session.user,
        })
      } catch {
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
        })
      }
    }

    checkAuth()
  }, [])

  if (authState.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white dark:bg-[#020617]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600 dark:text-gray-400">Checking authentication...</p>
        </div>
      </div>
    )
  }

  if (!authState.isAuthenticated) {
    const returnTo = location.pathname
    return <Navigate to={`/signin?redirect=${encodeURIComponent(returnTo)}`} replace />
  }

  return <Outlet context={{ user: authState.user }} />
}
